import { apiCall, isBackendConfigured } from './api-call'
import { ListingGoal } from '../_types/quickpass-goal'

interface GoalAnswer {
  label: string
  value: string
}

/**
 * Loads the inquiry options (PM goals) for a listing, each with its
 * qualification checklist pre-marked against the renter's QuickPass data.
 * Returns an empty list when the backend isn't configured.
 *
 * @param {string} quickPassUserId
 * @param {string} inventoryId
 * @returns {Promise<ListingGoal[]>}
 */
const getListingGoals = async (
  quickPassUserId: string,
  inventoryId: string,
): Promise<ListingGoal[]> => {
  if (isBackendConfigured() === false) {
    return []
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getlistinggoals',
      data: { quickPassUserId, inventoryId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ListingGoal[]
  } catch (error) {
    console.warn('[goals] getListingGoals failed —', error)
    return []
  }
}

/**
 * Starts a goal-driven conversation. Resolves with the entityId of the
 * resulting message thread (so the caller can route to it) or null on failure.
 *
 * @param {string} quickPassUserId
 * @param {string} inventoryId
 * @param {string} intentCode
 * @param {string} goalName
 * @param {GoalAnswer[]} answers
 * @returns {Promise<{ entityId: string; reply: string } | null>}
 */
const startGoalConversation = async (
  quickPassUserId: string,
  inventoryId: string,
  intentCode: string,
  goalName: string,
  answers: GoalAnswer[],
  personalMessage: string,
  includeShareLink: boolean,
): Promise<{ entityId: string; reply: string } | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'startgoalconversation',
      data: {
        quickPassUserId,
        inventoryId,
        intentCode,
        goalName,
        answers,
        personalMessage,
        includeShareLink,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as { entityId: string; reply: string }
  } catch (error) {
    console.warn('[goals] startGoalConversation failed —', error)
    return null
  }
}

export { getListingGoals, startGoalConversation }
